/**
 * OrderHeader Component
 * Displays order number, date, status, customer details, and financial summary
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Calendar,
  User,
  Mail,
  Phone,
  FileText,
  CreditCard,
  Building2,
} from 'lucide-react';
import type { OrderDetails, OrderStatus } from '@/types/order';

interface OrderHeaderProps {
  order: OrderDetails;
}

/**
 * Get badge variant based on order status
 */
function getStatusBadgeVariant(status: OrderStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'DELIVERED':
      return 'default'; // Green
    case 'CONFIRMED':
    case 'READY':
    case 'DISPATCHED':
      return 'secondary'; // Blue/Cyan
    case 'PENDING':
    case 'PROCESSING':
      return 'outline'; // Yellow/Gray
    case 'CANCELLED':
      return 'destructive'; // Red
    default:
      return 'outline';
  }
}

/**
 * Format currency to INR
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date to readable format
 */
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function OrderHeader({ order }: OrderHeaderProps) {
  return (
    <Card className="border-slate-200 bg-gradient-to-br from-white to-blue-50 shadow-lg">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <CardTitle className="text-3xl font-bold text-slate-900">
              {order.orderNumber}
            </CardTitle>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Calendar className="h-4 w-4" />
              <span>Order Date: {formatDate(order.orderDate)}</span>
            </div>
          </div>
          <Badge
            variant={getStatusBadgeVariant(order.orderStatus)}
            className="text-sm px-4 py-1.5"
          >
            {order.orderStatus.replace('_', ' ')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Customer Information */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Customer Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
              <Building2 className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">Company Name</p>
                <p className="text-sm font-medium text-slate-900 truncate">
                  {order.customer.customerName}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
              <Phone className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">Phone</p>
                <p className="text-sm font-medium text-slate-900 truncate">
                  {order.customer.phone}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
              <Mail className="h-5 w-5 text-purple-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500">Email</p>
                <p className="text-sm font-medium text-slate-900 truncate">
                  {order.customer.email}
                </p>
              </div>
            </div>
            {order.customer.gstNumber && (
              <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                <FileText className="h-5 w-5 text-orange-600 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-500">GST Number</p>
                  <p className="text-sm font-medium text-slate-900 truncate">
                    {order.customer.gstNumber}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Contract Reference */}
        {order.contract && (
          <>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Contract Reference
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                  <FileText className="h-5 w-5 text-indigo-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500">Contract Number</p>
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {order.contract.contractNumber}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                  <Calendar className="h-5 w-5 text-cyan-600 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-500">Contract Date</p>
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {formatDate(order.contract.contractDate)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                  <Badge
                    variant={
                      order.contract.contractStatus === 'ACTIVE'
                        ? 'default'
                        : 'destructive'
                    }
                    className="h-6 self-center"
                  >
                    {order.contract.contractStatus}
                  </Badge>
                </div>
              </div>
            </div>
            <Separator />
          </>
        )}

        {/* Financial Summary */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Financial Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 font-medium">Subtotal</p>
              <p className="text-lg font-bold text-blue-900">
                {formatCurrency(order.gstBreakdown.subtotal)}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-xs text-green-700 font-medium">Discount</p>
              <p className="text-lg font-bold text-green-900">
                {formatCurrency(order.gstBreakdown.totalDiscount)}
              </p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700 font-medium">Total Tax</p>
              <p className="text-lg font-bold text-amber-900">
                {formatCurrency(order.gstBreakdown.totalTax)}
              </p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-700 font-medium">Grand Total</p>
              <p className="text-xl font-bold text-purple-900">
                {formatCurrency(order.gstBreakdown.totalAmount)}
              </p>
            </div>
          </div>

          {/* GST Breakdown */}
          <div className="grid grid-cols-3 gap-3 pt-2">
            {order.gstBreakdown.cgstAmount > 0 && (
              <div className="p-2 bg-white rounded border border-slate-200 text-center">
                <p className="text-xs text-slate-500">CGST</p>
                <p className="text-sm font-semibold text-slate-900">
                  {formatCurrency(order.gstBreakdown.cgstAmount)}
                </p>
              </div>
            )}
            {order.gstBreakdown.sgstAmount > 0 && (
              <div className="p-2 bg-white rounded border border-slate-200 text-center">
                <p className="text-xs text-slate-500">SGST</p>
                <p className="text-sm font-semibold text-slate-900">
                  {formatCurrency(order.gstBreakdown.sgstAmount)}
                </p>
              </div>
            )}
            {order.gstBreakdown.igstAmount > 0 && (
              <div className="p-2 bg-white rounded border border-slate-200 text-center">
                <p className="text-xs text-slate-500">IGST</p>
                <p className="text-sm font-semibold text-slate-900">
                  {formatCurrency(order.gstBreakdown.igstAmount)}
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
